
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AvatarUpload } from "@/components/AvatarUpload";

interface Profile {
  id: string;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface ProfileFormProps {
  userId: string;
  profile: Profile | null;
  onProfileUpdate: (profile: Profile) => void;
  email: string;
}

export function ProfileForm({ userId, profile, onProfileUpdate, email }: ProfileFormProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
    }
  }, [profile]);

  const updateProfile = async () => {
    if (!profile) return;

    setSavingProfile(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
        })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: "Perfil actualizado",
        description: "Tu perfil ha sido actualizado correctamente",
      });

      onProfileUpdate({ ...profile, first_name: firstName, last_name: lastName });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el perfil",
        variant: "destructive",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-4">
        <AvatarUpload 
          userId={userId}
          currentAvatarUrl={profile?.avatar_url}
          onAvatarUpdate={(url) => onProfileUpdate(profile ? {...profile, avatar_url: url} : { id: userId, avatar_url: url, first_name: null, last_name: null })}
          size="lg"
        />
        <p className="text-sm text-white/60">{email}</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="firstName" className="text-sm font-medium text-white/80">
            Nombre
          </label>
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Introduce tu nombre"
            className="bg-[#1A1B1E] border-[#2E2F33] text-white placeholder:text-white/40 h-11"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="lastName" className="text-sm font-medium text-white/80">
            Apellidos
          </label>
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Introduce tus apellidos"
            className="bg-[#1A1B1E] border-[#2E2F33] text-white placeholder:text-white/40 h-11"
          />
        </div>

        <Button 
          onClick={updateProfile}
          disabled={savingProfile}
          className="w-full bg-[#00F5D0] hover:bg-[#00F5D0]/90 text-black font-medium h-11"
        >
          {savingProfile ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Guardar Cambios
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
